import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Play, ArrowRight, RotateCcw, CheckCircle, Code, HelpCircle, Trophy } from 'lucide-react';
import { useAcademyStore } from '../../store/academyStore';

type Level = {
    id: number;
    description: string;
    property: string; // The specific CSS property focused on this level
    goal: React.CSSProperties; // The target styles for the container
    frogs: number; // Number of frogs
    pads: number; // Number of lilypads
    validValues: string[]; // List of valid CSS values for this level to check against (optional hint)
    defaultCode: string; // Starter code
};

const LEVELS: Level[] = [
    {
        id: 1,
        description: "Welcome to Flexbox Pond! Move the frog to the lilypad on the right using 'justify-content'.",
        property: "justify-content",
        goal: { justifyContent: 'flex-end' },
        frogs: 1,
        pads: 1,
        validValues: ['flex-end'],
        defaultCode: "justify-content: ",
    },
    {
        id: 2,
        description: "Great hop! Now help the 2 frogs find their lilypads in the center.",
        property: "justify-content",
        goal: { justifyContent: 'center' },
        frogs: 2,
        pads: 2,
        validValues: ['center'],
        defaultCode: "justify-content: ",
    },
    {
        id: 3,
        description: "Frogs need space! Spread them out evenly so there is space between them.",
        property: "justify-content",
        goal: { justifyContent: 'space-between' },
        frogs: 3,
        pads: 3,
        validValues: ['space-between'],
        defaultCode: "justify-content: ",
    },
    {
        id: 4,
        description: "These lilypads are at the bottom! Use 'align-items' to move the frogs down.",
        property: "align-items",
        goal: { alignItems: 'flex-end' },
        frogs: 3,
        pads: 3,
        validValues: ['flex-end'],
        defaultCode: "align-items: ",
    },
    {
        id: 5,
        description: "Last challenge! The frogs need to be in a column, not a row.",
        property: "flex-direction",
        goal: { flexDirection: 'column' },
        frogs: 3,
        pads: 3,
        validValues: ['column'],
        defaultCode: "flex-direction: ",
    },
];

export const FlexboxFroggy = () => {
    const { completeModule } = useAcademyStore();
    const [currentLevel, setCurrentLevel] = useState(0);
    const [code, setCode] = useState(LEVELS[0].defaultCode);
    const [userStyle, setUserStyle] = useState<React.CSSProperties>({});
    const [isSuccess, setIsSuccess] = useState(false);
    const [showHint, setShowHint] = useState(false);

    const level = LEVELS[currentLevel];

    useEffect(() => {
        // Reset state on level change
        setCode(level.defaultCode);
        setUserStyle({});
        setIsSuccess(false);
        setShowHint(false);
    }, [currentLevel]);

    const handleCodeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setCode(value);

        // Simple CSS parser
        const lines = value.split(';').filter(line => line.trim().length > 0);
        const newStyle: React.CSSProperties = { display: 'flex' }; // Always flex

        lines.forEach(line => {
            const [prop, val] = line.split(':');
            if (prop && val) {
                // Convert kebab-case to camelCase for React style
                const camelProp = prop.trim().replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                newStyle[camelProp as keyof React.CSSProperties] = val.trim() as any;
            }
        });

        setUserStyle(newStyle);
    };

    const checkSuccess = () => {
        // Compare userStyle with goal styles
        // This is a simplified check. A robust one would compare computed styles or specific properties.
        let match = true;
        Object.keys(level.goal).forEach(key => {
            // @ts-ignore
            if (userStyle[key] !== level.goal[key]) {
                match = false;
            }
        });

        if (match) {
            setIsSuccess(true);
            if (currentLevel === LEVELS.length - 1) {
                completeModule('flexbox', 100);
            }
        }
    };

    useEffect(() => {
        // Check success in real-time or debounced could be better, 
        // but let's check whenever userStyle changes to be responsive
        checkSuccess();
    }, [userStyle]);

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-white flex flex-col">
            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-green-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    <span className="text-slate-400 font-bold">Level {currentLevel + 1} of {LEVELS.length}</span>
                    <div className="flex gap-1">
                        {LEVELS.map((l, i) => (
                            <div
                                key={l.id}
                                className={`w-3 h-3 rounded-full ${i < currentLevel ? 'bg-green-500' : i === currentLevel ? 'bg-yellow-400 animate-pulse' : 'bg-slate-700'}`}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex-1 flex flex-col lg:flex-row h-[calc(100vh-80px)]">

                {/* Editor Panel (Left) */}
                <div className="w-full lg:w-1/3 bg-slate-800 p-8 flex flex-col border-r border-slate-700 shadow-xl z-10">
                    <div className="mb-6">
                        <h1 className="text-3xl font-black text-green-400 mb-2 font-comic tracking-wide">Flexbox Froggy</h1>
                        <p className="text-slate-300 leading-relaxed text-lg">{level.description}</p>
                    </div>

                    <div className="flex-1 flex flex-col">
                        <div className="bg-slate-900 rounded-t-xl p-3 flex justify-between items-center border-b border-slate-700">
                            <span className="text-xs font-mono text-slate-500 font-bold">style.css</span>
                            <span className="flex gap-1">
                                <span className="w-3 h-3 rounded-full bg-red-500/50"></span>
                                <span className="w-3 h-3 rounded-full bg-yellow-500/50"></span>
                                <span className="w-3 h-3 rounded-full bg-green-500/50"></span>
                            </span>
                        </div>
                        <div className="bg-slate-900 p-6 rounded-b-xl flex-1 font-mono text-lg relative overflow-hidden border border-slate-700 shadow-inner">
                            <div className="text-slate-500 mb-2">#pond {'{'}</div>
                            <div className="text-slate-500 pl-4 mb-2">display: flex;</div>
                            <textarea
                                value={code}
                                onChange={handleCodeChange}
                                className="w-full bg-transparent text-white outline-none pl-4 resize-none h-32 focus:bg-slate-800/50 transition-colors rounded"
                                spellCheck="false"
                            />
                            <div className="text-slate-500 mt-2">{'}'}</div>
                        </div>
                    </div>

                    <div className="mt-8 flex gap-4">
                        <button
                            onClick={() => setShowHint(!showHint)}
                            className="btn bg-slate-700 text-slate-300 hover:bg-slate-600 flex-1"
                        >
                            <HelpCircle size={18} /> Hint
                        </button>
                        {isSuccess && (
                            <motion.button
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                onClick={() => {
                                    if (currentLevel < LEVELS.length - 1) {
                                        setCurrentLevel(l => l + 1);
                                    }
                                }}
                                className="btn flex-1 bg-green-500 text-white hover:bg-green-600 shadow-lg shadow-green-500/30"
                            >
                                Next Level <ArrowRight size={18} />
                            </motion.button>
                        )}
                    </div>

                    {showHint && (
                        <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-yellow-200 text-sm">
                            Try using generic property <strong>{level.property}</strong> with values like <strong>{level.validValues.join(', ')}</strong>.
                        </div>
                    )}
                </div>

                {/* Visual Output (Right) */}
                <div className="w-full lg:w-2/3 bg-slate-900 relative overflow-hidden">
                    {/* Background Grid Pattern */}
                    <div className="absolute inset-0 opacity-10"
                        style={{
                            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                            backgroundSize: '20px 20px'
                        }}
                    />

                    <div className="absolute inset-8 rounded-3xl border-4 border-slate-700 bg-slate-800/50 overflow-hidden shadow-2xl">

                        {/* The Pond (User Input Styling) */}
                        <div
                            className="absolute inset-0 w-full h-full p-4 transition-all duration-500 ease-out z-20"
                            style={{ display: 'flex', ...userStyle }}
                        >
                            {[...Array(level.frogs)].map((_, i) => (
                                <motion.div
                                    key={`frog-${i}`}
                                    layout
                                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                                    className="w-24 h-24 flex items-center justify-center text-6xl drop-shadow-2xl relative z-10"
                                >
                                    🐸
                                    <div className="absolute -bottom-4 bg-green-500/30 text-green-300 text-xs px-2 py-0.5 rounded font-mono font-bold">
                                        .frog
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* The Lilypads (Goal Styling) */}
                        <div
                            className="absolute inset-0 w-full h-full p-4 transition-all duration-500 z-10 opacity-60"
                            style={{ display: 'flex', ...level.goal, gap: userStyle.gap }} // Match gap if any
                        >
                            {[...Array(level.pads)].map((_, i) => (
                                <div
                                    key={`pad-${i}`}
                                    className="w-24 h-24 flex items-center justify-center text-6xl grayscale brightness-150 rounded-full bg-green-500/20 border-4 border-green-500/30 shadow-inner"
                                >
                                    🌱
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Success Overlay */}
                <AnimatePresence>
                    {isSuccess && (
                        <motion.div
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.5, opacity: 0 }}
                            className="absolute top-1/2 left-2/3 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none"
                        >
                            <div className="bg-white text-green-600 px-8 py-6 rounded-3xl shadow-2xl text-center transform rotate-3 border-4 border-green-400">
                                <Trophy size={64} className="mx-auto mb-2 text-yellow-400 fill-yellow-400" />
                                <h2 className="text-4xl font-black font-comic mb-1">Level Complete!</h2>
                                <p className="font-bold text-slate-400">Froggy is happy!</p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

            </div>
        </div>
    );
};
